import { useContext, useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { useQuery } from "react-query";

import { getSubbedSubs, getUsersWithLicences } from "../../api";
import { DataFormFieldProvider } from "../../contexts/dataFormFieldContext";
import { UserDataContext } from "../../contexts/userDataContext";
import { formatUserNameWithEmailString } from "../../utils";
import {
	FieldCheckbox,
	FieldDate,
	FieldDropdown,
	FieldDropdownMulti,
} from "..";

export const UserLicenceAssignmentForm = ({ currentFormData, setFormData }) => {
	const { selectedOrg } = useContext(UserDataContext);
	const orgId = selectedOrg.id;

	const {
		data: subs,
		isLoading,
		isFetching,
		error,
	} = useQuery([Object.keys({ getSubbedSubs })[0], orgId], () =>
		getSubbedSubs({ subscriberOrgId: orgId }),
	);

	const {
		data: usersResponse,
		isLoadingUsers,
		isFetchingUsers,
		errorUsers,
	} = useQuery([Object.keys({ getUsersWithLicences })[0], orgId], () =>
		getUsersWithLicences({ orgId, includeAvatars: true }),
	);

	const newSubs = useMemo(() => {
		if (subs) {
			const products = subs.results.filter(
				(sub) =>
					sub.type.includes("Product") &&
					sub.isEnabled &&
					new Date(sub.expirationDateUtc) > new Date(),
			);

			const productLabelFn = ({
				name,
				usedLicenceCount,
				numberOfLicences,
			}) => `${name} (${usedLicenceCount}/${numberOfLicences})`;

			return products.map(
				({ id, name, usedLicenceCount, numberOfLicences }) => ({
					id: id,
					value: name,
					label: productLabelFn({
						name,
						usedLicenceCount,
						numberOfLicences,
					}),
				}),
			);
		}
		return [];
	}, [subs]);

	const newUsers = useMemo(() => {
		if (usersResponse)
			return usersResponse.results.map(({ user, tags }) => ({
				id: user.email,
				value: user.email,
				label: formatUserNameWithEmailString(user),
				tags: tags,
			}));
		return [];
	}, [usersResponse]);

	const [subscriptionId, setSubscriptionId] = useState(newSubs[0]?.id);
	const subDate = subs.results.find((sub) => sub.id === subscriptionId);

	const [users, setUsers] = useState([]);
	const [isChecked, setIsChecked] = useState(false);
	const [date, setDate] = useState(new Date(subDate?.expirationDateUtc));

	useEffect(() => {
		if (isChecked === false) setDate(new Date(subDate?.expirationDateUtc));
	}, [isChecked, subDate]);

	useEffect(() => {
		setFormData("subscriptionId")(subscriptionId);
		setFormData("userEmails")(users);
	}, [subscriptionId, users]);

	return (
		<Form
			className={"my-3"}
			data-testid="test-dataForm"
			onSubmit={(e) => {
				e.preventDefault();
				// onSubmit(e);
			}}
		>
			<DataFormFieldProvider
				key="prov-1"
				isVerticalLayout={false}
				hasFieldTitle={true}
				fieldSetting={{ title: "Select Subscription" }}
			>
				<FieldDropdown
					fieldData={subscriptionId}
					setValue={setSubscriptionId}
					dropdownData={newSubs}
					title="Subscription"
					isDisabled={false}
					isMandatory={true}
					placeholder="Select a subscription"
					tooltip={false}
					error={false}
				/>
			</DataFormFieldProvider>
			<DataFormFieldProvider
				key="prov-2"
				isVerticalLayout={false}
				hasFieldTitle={true}
				fieldSetting={{ title: "Select Users" }}
			>
				<FieldDropdownMulti
					fieldData={users}
					dropdownData={newUsers}
					selectedRows={currentFormData.selectedRows}
					isSelectedRowValues={true}
					title="Users"
					setValue={setUsers}
					isDisabled={false}
					placeholder={"Select Users"}
					tooltip={false}
					error={false}
				/>
			</DataFormFieldProvider>
			<DataFormFieldProvider
				key="prov-3"
				isVerticalLayout={false}
				hasFieldTitle={true}
				fieldSetting={{
					title: "Custom Licence Expiration Date (Optional)",
				}}
			>
				<FieldCheckbox
					fieldData={isChecked}
					setValue={setIsChecked}
					isDisabled={false}
					title="Custom Date from Subscription Expiration"
					error={false}
				/>
			</DataFormFieldProvider>
			<DataFormFieldProvider
				key="prov-4"
				isVerticalLayout={false}
				hasFieldTitle={true}
				fieldSetting={{
					title: isChecked
						? "Licence Exp. Date"
						: "Subscription Exp. Date",
				}}
			>
				<FieldDate
					fieldData={date}
					setValue={setDate}
					isDisabled={!isChecked}
					placeholder={false}
					title="Date"
					tooltip={false}
					error={false}
				/>
			</DataFormFieldProvider>
		</Form>
	);
};
